import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { DashboardData } from '../types';

type Classification = 'all' | 'frontline' | 'backup';

type BreakdownRow = {
  item_type_id: string;
  item_type_name: string;
  count: number;
  estimated_cost: number;
};

type ChartBucket = {
  key: string;
  label: string;
  total: number;
  total_estimated_cost: number;
  breakdown: BreakdownRow[];
};

type HoverState = {
  key: string;
  x: number;
  y: number;
};

type ExpiringItem = DashboardData['expiry_chart']['item_details'][number];

function horizonLabel(years: number): string {
  if (years === 1) return '< 1 Year';
  if (years === 2) return '< 2 Years';
  if (years === 5) return '< 5 Years';
  return `< ${years} Years`;
}

function classificationLabel(value: Classification): string {
  if (value === 'frontline') return 'Frontline';
  if (value === 'backup') return 'Backup';
  return 'All Gear';
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function quarterKeyFromMonth(monthKey: string): string {
  const [year, month] = monthKey.split('-').map(Number);
  const quarter = Math.floor((month - 1) / 3) + 1;
  return `${year}-Q${quarter}`;
}

function quarterLabelFromKey(key: string): string {
  const [year, quarter] = key.split('-Q');
  return `Q${quarter} ${year}`;
}

function aggregateBreakdown(rows: BreakdownRow[]): BreakdownRow[] {
  const map = new Map<string, BreakdownRow>();
  for (const row of rows) {
    const existing = map.get(row.item_type_id);
    if (!existing) {
      map.set(row.item_type_id, { ...row });
      continue;
    }
    existing.count += row.count;
    existing.estimated_cost += row.estimated_cost;
  }

  return Array.from(map.values()).sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.item_type_name.localeCompare(b.item_type_name);
  });
}

function buildDisplayBuckets(
  months: DashboardData['expiry_chart']['months'],
  horizonYears: number,
  expiresYear: string
): { granularity: 'month' | 'quarter'; buckets: ChartBucket[] } {
  const shouldUseQuarter = !expiresYear && horizonYears > 2 && months.length > 24;

  if (!shouldUseQuarter) {
    return {
      granularity: 'month',
      buckets: months.map(month => ({
        key: month.month_key,
        label: month.label,
        total: month.total,
        total_estimated_cost: month.total_estimated_cost,
        breakdown: month.breakdown,
      })),
    };
  }

  const quarterMap = new Map<string, ChartBucket>();
  for (const month of months) {
    const quarterKey = quarterKeyFromMonth(month.month_key);
    const existing = quarterMap.get(quarterKey);
    if (!existing) {
      quarterMap.set(quarterKey, {
        key: quarterKey,
        label: quarterLabelFromKey(quarterKey),
        total: month.total,
        total_estimated_cost: month.total_estimated_cost,
        breakdown: [...month.breakdown],
      });
      continue;
    }

    existing.total += month.total;
    existing.total_estimated_cost += month.total_estimated_cost;
    existing.breakdown = aggregateBreakdown([...existing.breakdown, ...month.breakdown]);
  }

  return {
    granularity: 'quarter',
    buckets: Array.from(quarterMap.values()),
  };
}

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync filter state with URL search params
  const horizonYears = parseInt(searchParams.get('horizon') || '1', 10) || 1;
  const classification = (searchParams.get('class') || 'all') as Classification;
  const itemTypeId = searchParams.get('type') || '';
  const expiresYear = searchParams.get('expires') || '';

  const setFilter = (key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  };

  const [selectedBucketKey, setSelectedBucketKey] = useState<string | null>(null);
  const [expandedTypeId, setExpandedTypeId] = useState<string | null>(null);
  const [hoverState, setHoverState] = useState<HoverState | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [coverage, setCoverage] = useState<{ configured: boolean; totalMembers: number; fullyEquipped: number } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('horizon_years', String(horizonYears));
    params.set('gear_classification', classification);
    if (itemTypeId) params.set('item_type_id', itemTypeId);
    if (expiresYear) params.set('expires_year', expiresYear);

    setLoading(true);
    api.get<DashboardData>(`/dashboard?${params.toString()}`)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [horizonYears, classification, itemTypeId, expiresYear]);

  useEffect(() => {
    api.get<{ configured: boolean; totalMembers: number; fullyEquipped: number }>('/members/coverage/summary')
      .then(setCoverage)
      .catch(() => {}); // silently ignore if not available
  }, []);

  const months = useMemo(() => data?.expiry_chart.months || [], [data]);
  const { granularity, buckets } = useMemo(
    () => buildDisplayBuckets(months, horizonYears, expiresYear),
    [months, horizonYears, expiresYear]
  );

  useEffect(() => {
    if (selectedBucketKey && !buckets.some(bucket => bucket.key === selectedBucketKey)) {
      setSelectedBucketKey(null);
    }
    if (hoverState && !buckets.some(bucket => bucket.key === hoverState.key)) {
      setHoverState(null);
    }
  }, [buckets, selectedBucketKey, hoverState]);

  useEffect(() => {
    setExpandedTypeId(null);
  }, [selectedBucketKey, horizonYears, classification, itemTypeId, expiresYear]);

  const maxValue = Math.max(1, ...buckets.map(bucket => bucket.total));
  const labelStep = buckets.length > 20 ? 3 : buckets.length > 14 ? 2 : 1;

  const selectedBucket = selectedBucketKey
    ? buckets.find(bucket => bucket.key === selectedBucketKey) || null
    : null;
  const hoveredBucket = hoverState
    ? buckets.find(bucket => bucket.key === hoverState.key) || null
    : null;

  const hoveredBreakdown = useMemo(() => {
    if (!hoveredBucket) return [];
    return [...hoveredBucket.breakdown]
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.item_type_name.localeCompare(b.item_type_name);
      })
      .slice(0, 6);
  }, [hoveredBucket]);

  const detailItems = useMemo(() => {
    const items = data?.expiry_chart.item_details || [];
    if (!selectedBucket) return items;

    return items.filter(item => {
      const bucketKey = granularity === 'quarter'
        ? quarterKeyFromMonth(item.month_key)
        : item.month_key;
      return bucketKey === selectedBucket.key;
    });
  }, [data, selectedBucket, granularity]);

  const detailItemsByType = useMemo(() => {
    const map = new Map<string, ExpiringItem[]>();
    for (const item of detailItems) {
      if (!map.has(item.item_type_id)) {
        map.set(item.item_type_id, []);
      }
      map.get(item.item_type_id)!.push(item);
    }

    for (const rows of Array.from(map.values())) {
      rows.sort((a, b) => {
        if (a.expiry_date !== b.expiry_date) return a.expiry_date.localeCompare(b.expiry_date);
        return a.assigned_name.localeCompare(b.assigned_name);
      });
    }

    return map;
  }, [detailItems]);

  const detailSummary = useMemo(() => {
    const totalItems = detailItems.length;
    const totalEstimatedCost = detailItems.reduce((sum, item) => sum + item.estimated_cost, 0);
    const frontlineCount = detailItems.filter(item => item.gear_classification === 'frontline').length;
    const breakdown = aggregateBreakdown(
      detailItems.map(item => ({
        item_type_id: item.item_type_id,
        item_type_name: item.item_type_name,
        count: 1,
        estimated_cost: item.estimated_cost,
      }))
    );

    return {
      totalItems,
      totalEstimatedCost,
      frontlineCount,
      breakdown,
    };
  }, [detailItems]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading dashboard...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>Failed to load dashboard</div>;

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 16px', color: '#111' }}>Dashboard</h1>

      <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#6b7280' }}>
            Expiry Window
            <select
              value={horizonYears}
              disabled={Boolean(expiresYear)}
              onChange={e => setFilter('horizon', e.target.value === '1' ? '' : e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                background: expiresYear ? '#f3f4f6' : 'white',
                fontSize: 14,
                color: '#111',
              }}
            >
              {data.filter_options.horizon_options.map(y => (
                <option key={y} value={y}>{horizonLabel(y)}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#6b7280' }}>
            Expiry Year
            <select
              value={expiresYear}
              onChange={e => {
                setFilter('expires', e.target.value);
                setSelectedBucketKey(null);
              }}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: 14, color: '#111' }}
            >
              <option value="">All Years (Window)</option>
              {data.filter_options.expiry_year_options.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#6b7280' }}>
            Classification
            <select
              value={classification}
              onChange={e => {
                setFilter('class', e.target.value === 'all' ? '' : e.target.value);
                setSelectedBucketKey(null);
              }}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: 14, color: '#111' }}
            >
              {data.filter_options.classification_options.map(option => (
                <option key={option} value={option}>{classificationLabel(option)}</option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#6b7280' }}>
            Gear Type
            <select
              value={itemTypeId}
              onChange={e => {
                setFilter('type', e.target.value);
                setSelectedBucketKey(null);
              }}
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: 14, color: '#111' }}
            >
              <option value="">All Types</option>
              {data.filter_options.item_types.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
        </div>

        {expiresYear && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
            Expiry Window is disabled while a specific year is selected.
          </div>
        )}
      </div>

      {/* Coverage Summary */}
      {coverage?.configured && (
        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111' }}>Equipment Coverage</h2>
            <div style={{ display: 'flex', gap: 24, fontSize: 14 }}>
              <span style={{ color: '#6b7280' }}>
                Fully Equipped: <strong style={{ color: coverage.fullyEquipped === coverage.totalMembers ? '#166534' : '#b91c1c' }}>
                  {coverage.fullyEquipped}/{coverage.totalMembers}
                </strong> members
              </span>
              {coverage.totalMembers > coverage.fullyEquipped && (
                <span style={{ color: '#b91c1c', fontWeight: 600, fontSize: 13 }}>
                  {coverage.totalMembers - coverage.fullyEquipped} member{coverage.totalMembers - coverage.fullyEquipped !== 1 ? 's' : ''} missing gear
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111' }}>
            Expiring Gear Timeline ({granularity === 'quarter' ? 'Quarterly' : 'Monthly'})
          </h2>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
            <span style={{ color: '#6b7280' }}>Filtered Active: <strong style={{ color: '#111' }}>{data.summary.total_active_items}</strong></span>
            <span style={{ color: '#6b7280' }}>Expiring In View: <strong style={{ color: '#111' }}>{detailSummary.totalItems}</strong></span>
          </div>
        </div>

        <div style={{ marginBottom: 8, fontSize: 12, color: '#6b7280' }}>
          Hover a bar to preview details. Click a bar to lock that period&apos;s stats below.
        </div>

        {buckets.every(bucket => bucket.total === 0) ? (
          <div style={{ padding: '28px 12px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
            No gear expires within the selected range.
          </div>
        ) : (
          <div style={{ paddingBottom: 8 }}>
            <div style={{ width: '100%', display: 'flex', alignItems: 'flex-end', gap: buckets.length > 18 ? 4 : 6, height: 280 }}>
              {buckets.map((bucket, idx) => {
                const isSelected = selectedBucketKey === bucket.key;
                const barHeight = bucket.total > 0
                  ? Math.max(14, Math.round((bucket.total / maxValue) * 210))
                  : 6;

                return (
                  <button
                    key={bucket.key}
                    type="button"
                    onMouseEnter={e => setHoverState({ key: bucket.key, x: e.clientX, y: e.clientY })}
                    onMouseMove={e => setHoverState({ key: bucket.key, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHoverState(null)}
                    onClick={() => setSelectedBucketKey(prev => prev === bucket.key ? null : bucket.key)}
                    style={{
                      flex: '1 1 0',
                      minWidth: 0,
                      border: 'none',
                      background: 'transparent',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        maxWidth: 34,
                        height: barHeight,
                        borderRadius: 6,
                        background: bucket.total > 0 ? (isSelected ? '#b45309' : '#ea580c') : '#e5e7eb',
                        border: isSelected ? '2px solid #7c2d12' : '1px solid transparent',
                        boxSizing: 'border-box',
                        transition: 'all 0.15s ease',
                      }}
                    />
                    <div style={{ fontSize: 10, color: '#6b7280', textAlign: 'center', lineHeight: 1.2, minHeight: 24 }}>
                      {idx % labelStep === 0 ? bucket.label : ''}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111' }}>
              {selectedBucket ? `${selectedBucket.label} Details` : `All ${granularity === 'quarter' ? 'Quarters' : 'Months'} In View`}
            </h3>
            {selectedBucket && (
              <button
                type="button"
                onClick={() => setSelectedBucketKey(null)}
                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}
              >
                Clear Selection
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Items Expiring</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#111' }}>{detailSummary.totalItems}</div>
            </div>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Frontline Gear Expiring</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#111' }}>{detailSummary.frontlineCount}</div>
            </div>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Estimated Replacement Cost (View)</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#111' }}>{formatCurrency(detailSummary.totalEstimatedCost)}</div>
            </div>
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Overall Active Inventory Value</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#111' }}>{formatCurrency(data.summary.overall_active_estimated_cost)}</div>
            </div>
          </div>

          {detailSummary.breakdown.length > 0 && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: '#f9fafb', padding: '10px 12px', fontWeight: 600, color: '#374151' }}>Type Breakdown</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#ffffff', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6b7280' }}>Gear Type</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', color: '#6b7280' }}>Items</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px', color: '#6b7280' }}>Estimated Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {detailSummary.breakdown.map(row => {
                    const isExpanded = expandedTypeId === row.item_type_id;
                    const typeItems = detailItemsByType.get(row.item_type_id) || [];

                    return (
                      <React.Fragment key={row.item_type_id}>
                        <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '4px 6px 4px 12px', color: '#111', fontWeight: 500 }}>
                            <button
                              type="button"
                              onClick={() => setExpandedTypeId(prev => prev === row.item_type_id ? null : row.item_type_id)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: '6px 0',
                                cursor: 'pointer',
                                color: '#111',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <span style={{ color: '#6b7280', width: 10, display: 'inline-block' }}>{isExpanded ? '▾' : '▸'}</span>
                              <span>{row.item_type_name}</span>
                            </button>
                          </td>
                          <td style={{ padding: '10px 12px', color: '#111', textAlign: 'right' }}>{row.count}</td>
                          <td style={{ padding: '10px 12px', color: '#111', textAlign: 'right' }}>{formatCurrency(row.estimated_cost)}</td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td colSpan={3} style={{ padding: '8px 12px 12px', background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                              {typeItems.length === 0 ? (
                                <div style={{ fontSize: 12, color: '#6b7280' }}>No expiring items in this view.</div>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                                  <thead>
                                    <tr style={{ background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                                      <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280' }}>Assigned To</th>
                                      <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280' }}>Expiry</th>
                                      <th style={{ textAlign: 'left', padding: '8px 10px', color: '#6b7280' }}>Classification</th>
                                      <th style={{ textAlign: 'right', padding: '8px 10px', color: '#6b7280' }}>Estimated Cost</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {typeItems.map(item => (
                                      <tr key={item.item_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '8px 10px', color: '#111' }}>{item.assigned_name}</td>
                                        <td style={{ padding: '8px 10px', color: '#111' }}>{item.expiry_label}</td>
                                        <td style={{ padding: '8px 10px', color: '#111' }}>
                                          {item.gear_classification === 'frontline'
                                            ? 'Frontline'
                                            : item.gear_classification === 'backup'
                                              ? 'Backup'
                                              : 'Unspecified'}
                                        </td>
                                        <td style={{ padding: '8px 10px', color: '#111', textAlign: 'right' }}>{formatCurrency(item.estimated_cost)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {hoverState && hoveredBucket && (
        <div
          style={{
            position: 'fixed',
            left: `${Math.max(12, Math.min(hoverState.x + 14, window.innerWidth - 300))}px`,
            top: `${Math.max(12, hoverState.y - 16)}px`,
            transform: 'translateY(-100%)',
            background: '#111827',
            color: 'white',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 12,
            lineHeight: 1.35,
            boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
            zIndex: 1000,
            pointerEvents: 'none',
            maxWidth: 280,
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{hoveredBucket.label}</div>
          <div style={{ color: '#cbd5e1', marginBottom: 6 }}>
            {hoveredBucket.total} item{hoveredBucket.total !== 1 ? 's' : ''} expiring • {formatCurrency(hoveredBucket.total_estimated_cost)}
          </div>
          {hoveredBreakdown.map(item => (
            <div key={item.item_type_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ color: '#e5e7eb' }}>{item.item_type_name}</span>
              <span style={{ color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                {item.count} • {formatCurrency(item.estimated_cost)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
